import type {
  CategoryView,
  ModifierGroupView,
  OutletSummary,
  ProductView,
  VariantView,
} from "@kopi/contracts";
import {
  MAX_IMAGE_UPLOAD_BYTES,
  MAX_IMAGE_UPLOAD_LABEL,
  MAX_PRODUCT_MODIFIER_GROUPS,
} from "@kopi/contracts/client";
import type { PreparationStation, SalesChannel } from "@kopi/contracts/codes";
import { zonedLocalToUtc } from "@kopi/domain";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@kopi/ui/components/alert-dialog";
import { Badge } from "@kopi/ui/components/badge";
import { Button } from "@kopi/ui/components/button";
import {
  Card,
  CardAction,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@kopi/ui/components/card";
import { Combobox } from "@kopi/ui/components/combobox";
import { DatePicker } from "@kopi/ui/components/date-picker";
import { Input } from "@kopi/ui/components/input";
import { Label } from "@kopi/ui/components/label";
import { OptionSelect } from "@kopi/ui/components/option-select";
import { ResponsiveDialog } from "@kopi/ui/components/responsive-dialog";
import { toast } from "@kopi/ui/components/sonner";
import {
  type QueryClient,
  useInfiniteQuery,
  useMutation,
  useQuery,
  useQueryClient,
  useSuspenseQuery,
} from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { ArrowDownIcon, ArrowUpIcon, PencilIcon, PlusIcon } from "lucide-react";
import { type ChangeEvent, type FormEvent, useId, useRef, useState } from "react";
import { ActiveToggle } from "@/components/active-toggle";
import {
  type CatalogueT,
  ChannelPicker,
  catalogueError,
  FormError,
  KIND_KEYS,
  kindOptions,
  NameEnField,
  NO_STATION,
  ProductPhoto,
  selectionRule,
  stationOptions,
} from "@/components/catalogue";
import { useCurrentOutlet } from "@/components/current-outlet";
import { LoadMore } from "@/components/infinite-list";
import { Page, PageHeader } from "@/components/page-header";
import { Recipes } from "@/components/recipes";
import { catalogue } from "@/i18n/messages/catalogue";
import { useMessages } from "@/i18n/provider";
import { canAt } from "@/lib/access";
import { api, unwrap } from "@/lib/api";
import { CHANNEL_KEYS } from "@/lib/channels";
import {
  availabilityQuery,
  categoryLabel,
  fetchCategoryChoices,
  fetchModifierGroupChoices,
  meScopesQuery,
  outletsQuery,
  pricesQuery,
  productQuery,
  productsKey,
} from "@/lib/queries";
import { defaultScheduleTime } from "@/lib/schedule";

export const Route = createFileRoute("/_authed/catalogue/$productId")({
  staticData: {
    section: "workspace",
    parent: { label: "nav.catalogue", to: "/catalogue" },
    crumb: "page.product",
  },
  loader: ({ context, params }) =>
    Promise.all([
      context.queryClient.ensureQueryData(productQuery(params.productId)),
      context.queryClient.ensureQueryData(outletsQuery),
    ]),
  component: ProductPage,
});

async function storeProduct(queryClient: QueryClient, product: ProductView) {
  queryClient.setQueryData(productQuery(product.id).queryKey, product);
  await queryClient.invalidateQueries({ queryKey: productsKey });
}

function ProductPage() {
  const { t } = useMessages(catalogue);
  const { productId } = Route.useParams();
  const { data: product } = useSuspenseQuery(productQuery(productId));
  const queryClient = useQueryClient();
  const [editing, setEditing] = useState(false);
  // (De)activating is separate from the edit dialog: it never touches unsaved edits.
  const toggle = useMutation({
    mutationFn: async () =>
      unwrap(await api.v1.products({ productId: product.id }).patch({ active: !product.active })),
    onSuccess: (updated) => storeProduct(queryClient, updated),
    onError: (error) => toast.error(catalogueError(t, error)),
  });

  return (
    <Page width="narrow">
      <PageHeader
        eyebrow={`${t("nav.catalogue")} · ${product.categoryName}`}
        title={
          <span className="flex flex-wrap items-center gap-3">
            {product.name}
            {!product.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
          </span>
        }
        subtitle={`${t(KIND_KEYS[product.kind])} · ${product.channels.map((c) => t(CHANNEL_KEYS[c])).join(", ")}`}
        actions={
          <>
            <Button type="button" variant="outline" onClick={() => setEditing(true)}>
              <PencilIcon />
              {t("catalogue.product.edit")}
            </Button>
            {/* Products are never deleted: orders keep referring to them (CAT-01). */}
            <ActiveToggle
              active={product.active}
              title={t("catalogue.product.deactivateTitle", { name: product.name })}
              description={t("catalogue.product.deactivateHint")}
              confirmLabel={t("catalogue.product.deactivate")}
              disabled={toggle.isPending}
              onChange={() => toggle.mutate()}
            />
          </>
        }
      />
      {product.description && <p className="-mt-2 text-muted-foreground">{product.description}</p>}
      <PhotoSection product={product} />
      <Variants product={product} />
      <Modifiers product={product} />
      <Recipes product={product} />
      <OutletPricing product={product} />
      {editing && <ProductDialog product={product} onClose={() => setEditing(false)} />}
    </Page>
  );
}

const ACCEPTED_PHOTO_TYPES = "image/jpeg,image/png,image/webp";

/** Product photo (R1-10): the server validates and re-encodes it; this only previews and uploads. */
function PhotoSection(props: { product: ProductView }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const fileInput = useRef<HTMLInputElement>(null);
  const [tooLarge, setTooLarge] = useState(false);
  const productId = props.product.id;

  const upload = useMutation({
    mutationFn: async (file: File) => unwrap(await api.v1.products({ productId }).image.put({ file })),
    onSuccess: (updated) => storeProduct(queryClient, updated),
  });
  const remove = useMutation({
    mutationFn: async () => unwrap(await api.v1.products({ productId }).image.delete()),
    onSuccess: (updated) => storeProduct(queryClient, updated),
  });

  function pick(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setTooLarge(file.size > MAX_IMAGE_UPLOAD_BYTES);
    remove.reset();
    if (file.size <= MAX_IMAGE_UPLOAD_BYTES) upload.mutate(file);
  }

  const hasPhoto = Boolean(props.product.imageUrl);
  const busy = upload.isPending || remove.isPending;

  return (
    <Card className="flex-col gap-4 px-6 sm:flex-row sm:items-center">
      <ProductPhoto
        url={props.product.imageUrl}
        alt={hasPhoto ? t("catalogue.photo.of", { name: props.product.name }) : undefined}
        className="size-40"
      />
      <div className="flex flex-col gap-2">
        <h2 className="font-semibold">{t("catalogue.photo.title")}</h2>
        <p className="text-muted-foreground text-sm">
          {t("catalogue.photo.hint", { max: MAX_IMAGE_UPLOAD_LABEL })}
        </p>
        <Input
          ref={fileInput}
          type="file"
          accept={ACCEPTED_PHOTO_TYPES}
          className="hidden"
          tabIndex={-1}
          aria-hidden
          onChange={pick}
        />
        <div className="flex flex-wrap gap-2">
          <Button type="button" variant="outline" disabled={busy} onClick={() => fileInput.current?.click()}>
            {t(
              upload.isPending
                ? "catalogue.photo.uploading"
                : hasPhoto
                  ? "catalogue.photo.replace"
                  : "catalogue.photo.upload",
            )}
          </Button>
          {hasPhoto && (
            <AlertDialog>
              <AlertDialogTrigger asChild>
                <Button type="button" variant="ghost" disabled={busy}>
                  {t("catalogue.photo.remove")}
                </Button>
              </AlertDialogTrigger>
              <AlertDialogContent>
                <AlertDialogHeader>
                  <AlertDialogTitle>{t("catalogue.photo.removeTitle")}</AlertDialogTitle>
                  <AlertDialogDescription>
                    {t("catalogue.photo.removeHint", { name: props.product.name })}
                  </AlertDialogDescription>
                </AlertDialogHeader>
                <AlertDialogFooter>
                  <AlertDialogCancel>{t("common.cancel")}</AlertDialogCancel>
                  <AlertDialogAction variant="destructive" onClick={() => remove.mutate()}>
                    {t("catalogue.photo.remove")}
                  </AlertDialogAction>
                </AlertDialogFooter>
              </AlertDialogContent>
            </AlertDialog>
          )}
        </div>
        {tooLarge && (
          <p role="alert" className="text-destructive text-sm">
            {t("catalogue.error.photoTooLarge", { max: MAX_IMAGE_UPLOAD_LABEL })}
          </p>
        )}
        <FormError error={upload.error ?? remove.error} />
      </div>
    </Card>
  );
}

const categoryChoices = fetchCategoryChoices();
const categoryValue = (c: CategoryView) => c.id;

function ProductDialog(props: { product: ProductView; onClose: () => void }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [form, setForm] = useState(props.product);
  const ids = { name: useId(), category: useId(), kind: useId(), station: useId(), description: useId() };
  const set = <K extends keyof ProductView>(key: K, value: ProductView[K]) =>
    setForm((f) => ({ ...f, [key]: value }));

  const save = useMutation({
    mutationFn: async (
      change: Partial<
        Pick<ProductView, "name" | "nameEn" | "categoryId" | "kind" | "station" | "channels" | "description">
      >,
    ) => unwrap(await api.v1.products({ productId: props.product.id }).patch(change)),
    onSuccess: async (updated) => {
      await storeProduct(queryClient, updated);
      toast.success(t("catalogue.product.saved"));
      props.onClose();
    },
  });
  const incomplete = !form.name.trim() || form.channels.length === 0;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (save.isPending || incomplete) return;
    save.mutate({
      name: form.name,
      nameEn: form.nameEn,
      categoryId: form.categoryId,
      kind: form.kind,
      station: form.station,
      channels: form.channels,
      description: form.description?.trim() || null,
    });
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("catalogue.product.edit")}
      description={t("catalogue.product.editHint")}
      dismissible={!save.isPending}
      className="md:max-w-2xl"
      footer={
        <Button type="submit" form="edit-product" disabled={save.isPending || incomplete}>
          {t(save.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="edit-product" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor={ids.name}>{t("common.name")}</Label>
            <Input id={ids.name} value={form.name} onChange={(e) => set("name", e.target.value)} />
          </div>
          <div className="sm:col-span-2">
            <NameEnField value={form.nameEn ?? ""} onChange={(value) => set("nameEn", value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.category}>{t("catalogue.category")}</Label>
            <Combobox
              id={ids.category}
              fetchPage={categoryChoices}
              getValue={categoryValue}
              getLabel={categoryLabel}
              value={form.categoryId}
              // The saved category's name comes with the product; a newly picked one is remembered.
              selectedLabel={form.categoryName}
              onChange={(_, picked) => {
                if (picked) setForm((f) => ({ ...f, categoryId: picked.id, categoryName: picked.name }));
              }}
              searchPlaceholder={t("catalogue.searchCategories")}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.kind}>{t("catalogue.kind")}</Label>
            <OptionSelect
              id={ids.kind}
              options={kindOptions(t)}
              value={form.kind}
              onValueChange={(kind) => set("kind", kind)}
            />
          </div>
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor={ids.station}>{t("catalogue.station")}</Label>
            <OptionSelect
              id={ids.station}
              options={stationOptions(t)}
              value={form.station ?? NO_STATION}
              onValueChange={(value) =>
                set("station", value === NO_STATION ? null : (value as PreparationStation))
              }
            />
            <p className="text-muted-foreground text-xs">{t("catalogue.stationHint")}</p>
          </div>
        </div>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.description}>{t("catalogue.product.description")}</Label>
          <Input
            id={ids.description}
            value={form.description ?? ""}
            onChange={(e) => set("description", e.target.value)}
          />
        </div>
        <ChannelPicker value={form.channels} onChange={(c: SalesChannel[]) => set("channels", c)} />
        <FormError error={save.error} />
      </form>
    </ResponsiveDialog>
  );
}

function Variants(props: { product: ProductView }) {
  const { t } = useMessages(catalogue);
  const [dialog, setDialog] = useState<{ variant?: VariantView } | null>(null);
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("catalogue.variants")}</CardTitle>
        <CardDescription>{t("catalogue.variants.hint")}</CardDescription>
        <CardAction>
          <Button type="button" variant="outline" onClick={() => setDialog({})}>
            <PlusIcon />
            {t("catalogue.addVariant")}
          </Button>
        </CardAction>
      </CardHeader>
      <CardContent>
        {/* Not virtualised (CODING_STANDARDS §4 exception): a product has a handful of variants. */}
        <ul className="flex flex-col divide-y">
          {props.product.variants.map((variant) => (
            <VariantItem key={variant.id} variant={variant} onEdit={() => setDialog({ variant })} />
          ))}
        </ul>
      </CardContent>
      {dialog && (
        <VariantDialog
          productId={props.product.id}
          variant={dialog.variant}
          onClose={() => setDialog(null)}
        />
      )}
    </Card>
  );
}

function VariantItem(props: { variant: VariantView; onEdit: () => void }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const toggle = useMutation({
    mutationFn: async (active: boolean) =>
      unwrap(await api.v1.variants({ variantId: props.variant.id }).patch({ active })),
    onSuccess: (updated) => storeProduct(queryClient, updated),
    onError: (error) => toast.error(catalogueError(t, error)),
  });

  return (
    <li className="flex flex-wrap items-center justify-between gap-3 py-3">
      <div className="min-w-0">
        <p className="flex items-center gap-2 truncate font-medium">
          {props.variant.name}
          {!props.variant.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
        </p>
        <p className="truncate text-muted-foreground text-sm">
          {t("catalogue.variants.codes", {
            sku: props.variant.sku ?? "—",
            barcode: props.variant.barcode ?? "—",
          })}
        </p>
      </div>
      <div className="flex flex-wrap gap-2">
        <Button type="button" size="sm" variant="outline" onClick={props.onEdit}>
          {t("catalogue.edit")}
        </Button>
        <ActiveToggle
          size="sm"
          active={props.variant.active}
          title={t("catalogue.variants.deactivateTitle", { name: props.variant.name })}
          description={t("catalogue.variants.deactivateHint")}
          confirmLabel={t("catalogue.variants.deactivate")}
          disabled={toggle.isPending}
          onChange={(active) => toggle.mutate(active)}
        />
      </div>
    </li>
  );
}

/** Add a variant (no `variant`) or edit one. */
function VariantDialog(props: { productId: string; variant?: VariantView | undefined; onClose: () => void }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [name, setName] = useState(props.variant?.name ?? "");
  const [nameEn, setNameEn] = useState(props.variant?.nameEn ?? "");
  const [sku, setSku] = useState(props.variant?.sku ?? "");
  const [barcode, setBarcode] = useState(props.variant?.barcode ?? "");
  const ids = { name: useId(), sku: useId(), barcode: useId() };
  const editing = props.variant !== undefined;

  const save = useMutation({
    mutationFn: async () => {
      const change = { name, nameEn, sku: sku || null, barcode: barcode || null };
      return props.variant
        ? unwrap(await api.v1.variants({ variantId: props.variant.id }).patch(change))
        : unwrap(await api.v1.products({ productId: props.productId }).variants.post(change));
    },
    onSuccess: async (updated) => {
      await storeProduct(queryClient, updated);
      toast.success(
        editing ? t("catalogue.variants.saved") : t("catalogue.variants.added", { name: name.trim() }),
      );
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!save.isPending && name.trim()) save.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={
        editing
          ? t("catalogue.variants.edit", { name: props.variant?.name ?? "" })
          : t("catalogue.addVariant")
      }
      description={t("catalogue.variants.formHint")}
      dismissible={!save.isPending}
      footer={
        <Button type="submit" form="variant-form" disabled={save.isPending || !name.trim()}>
          {t(save.isPending ? "common.saving" : "common.save")}
        </Button>
      }
    >
      <form id="variant-form" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={ids.name}>{t("catalogue.variantName")}</Label>
          <Input
            id={ids.name}
            placeholder={t("catalogue.variants.namePlaceholder")}
            value={name}
            onChange={(e) => setName(e.target.value)}
          />
        </div>
        <NameEnField value={nameEn} onChange={setNameEn} />
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.sku}>{t("catalogue.skuPlaceholder")}</Label>
            <Input id={ids.sku} value={sku} onChange={(e) => setSku(e.target.value)} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label htmlFor={ids.barcode}>{t("catalogue.barcodePlaceholder")}</Label>
            <Input
              id={ids.barcode}
              inputMode="numeric"
              value={barcode}
              onChange={(e) => setBarcode(e.target.value)}
            />
          </div>
        </div>
        <FormError error={save.error} />
      </form>
    </ResponsiveDialog>
  );
}

/** Modifier groups the product offers (R1-11), in the order the register shows them. */
function Modifiers(props: { product: ProductView }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [attaching, setAttaching] = useState(false);
  const groups = props.product.modifierGroups;
  const ids = groups.map((g) => g.id);
  const save = useMutation({
    mutationFn: async (groupIds: string[]) =>
      unwrap(await api.v1.products({ productId: props.product.id })["modifier-groups"].put({ groupIds })),
    onSuccess: (updated) => storeProduct(queryClient, updated),
    onError: (error) => toast.error(catalogueError(t, error)),
  });
  /** The list with the group at `index` swapped with its neighbour `by` places away. */
  const swapped = (index: number, by: number) => {
    const next = [...ids];
    [next[index], next[index + by]] = [next[index + by] ?? "", next[index] ?? ""];
    return next;
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("catalogue.tab.modifiers")}</CardTitle>
        <CardDescription>{t("catalogue.productModifiers.hint")}</CardDescription>
        {groups.length < MAX_PRODUCT_MODIFIER_GROUPS && (
          <CardAction>
            <Button type="button" variant="outline" onClick={() => setAttaching(true)}>
              <PlusIcon />
              {t("catalogue.productModifiers.attach")}
            </Button>
          </CardAction>
        )}
      </CardHeader>
      <CardContent>
        {groups.length === 0 ? (
          <p className="text-muted-foreground text-sm">{t("catalogue.productModifiers.none")}</p>
        ) : (
          // Not virtualised (CODING_STANDARDS §4 exception): a product offers at most 20 groups.
          <ul className="flex flex-col divide-y">
            {groups.map((group, index) => (
              <li key={group.id} className="flex flex-wrap items-center justify-between gap-3 py-3">
                <div className="min-w-0">
                  <p className="flex items-center gap-2 truncate font-medium">
                    {group.name}
                    {!group.active && <Badge variant="outline">{t("common.inactive")}</Badge>}
                  </p>
                  <p className="text-muted-foreground text-sm">
                    {group.active ? selectionRule(t, group) : t("catalogue.productModifiers.inactive")}
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={t("catalogue.productModifiers.moveUp", { name: group.name })}
                    disabled={index === 0 || save.isPending}
                    onClick={() => save.mutate(swapped(index, -1))}
                  >
                    <ArrowUpIcon />
                  </Button>
                  <Button
                    type="button"
                    size="icon"
                    variant="ghost"
                    aria-label={t("catalogue.productModifiers.moveDown", { name: group.name })}
                    disabled={index === groups.length - 1 || save.isPending}
                    onClick={() => save.mutate(swapped(index, 1))}
                  >
                    <ArrowDownIcon />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    disabled={save.isPending}
                    onClick={() => save.mutate(ids.filter((id) => id !== group.id))}
                  >
                    {t("catalogue.productModifiers.detach")}
                  </Button>
                </div>
              </li>
            ))}
          </ul>
        )}
      </CardContent>
      {attaching && <AttachModifierDialog product={props.product} onClose={() => setAttaching(false)} />}
    </Card>
  );
}

const modifierGroupValue = (g: ModifierGroupView) => g.id;
const modifierGroupLabel = (t: CatalogueT) => (g: ModifierGroupView) => `${g.name} · ${selectionRule(t, g)}`;

/** Picks an active group (searched on the server) and appends it to the product's list. */
function AttachModifierDialog(props: { product: ProductView; onClose: () => void }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [groupId, setGroupId] = useState<string | null>(null);
  const pickerId = useId();
  const attached = props.product.modifierGroups.map((g) => g.id);
  const already = groupId !== null && attached.includes(groupId);

  const attach = useMutation({
    mutationFn: async (id: string) =>
      unwrap(
        await api.v1
          .products({ productId: props.product.id })
          ["modifier-groups"].put({ groupIds: [...attached, id] }),
      ),
    onSuccess: async (updated) => {
      await storeProduct(queryClient, updated);
      toast.success(t("catalogue.productModifiers.attached"));
      props.onClose();
    },
  });

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!attach.isPending && groupId && !already) attach.mutate(groupId);
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("catalogue.productModifiers.attachTitle")}
      description={t("catalogue.productModifiers.attachHint", { name: props.product.name })}
      dismissible={!attach.isPending}
      footer={
        <Button type="submit" form="attach-modifier" disabled={attach.isPending || !groupId || already}>
          {t(attach.isPending ? "common.saving" : "catalogue.productModifiers.attachConfirm")}
        </Button>
      }
    >
      <form id="attach-modifier" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={pickerId}>{t("catalogue.modifiers.group")}</Label>
          <Combobox
            id={pickerId}
            fetchPage={fetchModifierGroupChoices}
            getValue={modifierGroupValue}
            getLabel={modifierGroupLabel(t)}
            value={groupId}
            onChange={setGroupId}
            placeholder={t("catalogue.productModifiers.pickGroup")}
            searchPlaceholder={t("catalogue.modifiers.search")}
            emptyText={t("catalogue.productModifiers.noActive")}
          />
          {already && (
            <p role="alert" className="text-destructive text-sm">
              {t("catalogue.productModifiers.already")}
            </p>
          )}
        </div>
        <FormError error={attach.error} />
      </form>
    </ResponsiveDialog>
  );
}

/** Prices and availability per outlet; managers only see outlets they may price. */
function OutletPricing(props: { product: ProductView }) {
  const { t } = useMessages(catalogue);
  const { data: scopes } = useSuspenseQuery(meScopesQuery);
  const { data: outlets } = useSuspenseQuery(outletsQuery);
  const { outlet: current } = useCurrentOutlet();
  const priceable = outlets.filter((o) => o.active && canAt(scopes, "catalogue.manage", o.id));
  // Starts at the shell's outlet when it may be priced here.
  const [outletId, setOutletId] = useState(
    priceable.find((o) => o.id === current?.id)?.id ?? priceable[0]?.id ?? "",
  );
  const outletSelectId = useId();
  const outlet = priceable.find((o) => o.id === outletId) ?? priceable[0];

  if (!outlet) return null;
  return (
    <Card>
      <CardHeader>
        <CardTitle>{t("catalogue.pricing.title")}</CardTitle>
        <CardDescription>{t("catalogue.pricing.hint")}</CardDescription>
      </CardHeader>
      <CardContent className="flex flex-col gap-4">
        <div className="flex flex-col gap-1.5 sm:max-w-xs">
          <Label htmlFor={outletSelectId}>{t("outlet.heading")}</Label>
          {/* A tenant's outlets are a small, bounded set (CODING_STANDARDS §5: OptionSelect is fine). */}
          <OptionSelect
            id={outletSelectId}
            options={priceable.map((o) => ({ value: o.id, label: o.name }))}
            value={outlet.id}
            onValueChange={setOutletId}
          />
        </div>
        <Availability key={outlet.id} product={props.product} outletId={outlet.id} />
        <ul className="flex flex-col divide-y">
          {props.product.variants
            .filter((v) => v.active)
            .map((variant) => (
              <VariantPrices
                key={`${outlet.id}:${variant.id}`}
                outlet={outlet}
                productId={props.product.id}
                variant={variant}
              />
            ))}
        </ul>
      </CardContent>
    </Card>
  );
}

/**
 * The outlet's stored availability flag (not whether it shows on the register, which also needs an
 * active product and a current price). Null means it was never listed there: the first price lists it.
 */
function Availability(props: { product: ProductView; outletId: string }) {
  const { t } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const query = availabilityQuery(props.outletId, props.product.id);
  const { data } = useQuery(query);
  const available = data?.available ?? null;
  const toggle = useMutation({
    mutationFn: async (next: boolean) =>
      unwrap(
        await api.v1
          .outlets({ outletId: props.outletId })
          .products({ productId: props.product.id })
          .availability.put({ available: next }),
      ),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: query.queryKey }),
  });

  const status = t(
    !props.product.active
      ? "catalogue.availability.productInactive"
      : available === false
        ? "catalogue.availability.hidden"
        : available === null
          ? "catalogue.availability.notListed"
          : "catalogue.availability.listed",
  );

  return (
    <div className="flex flex-wrap items-center justify-between gap-2 rounded-lg bg-muted p-3 text-sm">
      <span>{status}</span>
      {data && (
        <Button
          type="button"
          size="sm"
          variant="outline"
          disabled={toggle.isPending}
          onClick={() => toggle.mutate(available === false)}
        >
          {t(available === false ? "catalogue.availability.show" : "catalogue.availability.hide")}
        </Button>
      )}
    </div>
  );
}

/** A variant's price at the outlet: in force, upcoming and past; "Atur harga" opens the schedule dialog. */
function VariantPrices(props: { outlet: OutletSummary; productId: string; variant: VariantView }) {
  const { outlet } = props;
  const { t, format } = useMessages(catalogue);
  const history = useInfiniteQuery(pricesQuery(outlet.id, props.variant.id));
  const [pricing, setPricing] = useState(false);
  const now = new Date();
  // The server says which version is in force: scheduled ones may fill the first pages ahead of it.
  const versions = (history.data?.pages.flatMap((page) => page.items) ?? []).map((p) => ({
    ...p,
    amount: BigInt(p.amount),
    effectiveFrom: new Date(p.effectiveFrom),
  }));
  const inForce = history.data?.pages[0]?.current;
  const current = inForce ? { id: inForce.id, amount: BigInt(inForce.amount) } : undefined;
  const upcoming = versions
    .filter((p) => p.effectiveFrom > now)
    .sort((a, b) => a.effectiveFrom.getTime() - b.effectiveFrom.getTime());
  const past = versions.filter((p) => p.effectiveFrom <= now && p.id !== current?.id);

  return (
    <li className="flex flex-col gap-2 py-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="min-w-0">
          <p className="font-medium">{props.variant.name}</p>
          <p className="font-semibold text-lg tabular-nums">
            {current ? (
              format.money(current.amount)
            ) : (
              <span className="font-normal text-muted-foreground text-sm">{t("catalogue.pricing.none")}</span>
            )}
          </p>
        </div>
        <Button type="button" size="sm" variant="outline" onClick={() => setPricing(true)}>
          {t("catalogue.pricing.set")}
        </Button>
      </div>
      {upcoming.length > 0 && (
        <ul className="text-muted-foreground text-sm">
          {upcoming.map((p) => (
            <li key={p.id}>
              {t("catalogue.pricing.from", {
                price: format.money(p.amount),
                when: format.dateTime(p.effectiveFrom.toISOString(), outlet.timezone),
              })}
            </li>
          ))}
        </ul>
      )}
      {/* Not virtualised (CODING_STANDARDS §4 exception): a variant's price history at one outlet is short. */}
      {past.length > 0 && (
        <div className="flex flex-col gap-1">
          <p className="font-medium text-muted-foreground text-xs">{t("catalogue.pricing.history")}</p>
          <ul className="text-muted-foreground text-sm">
            {past.map((p) => (
              <li key={p.id}>
                {t("catalogue.pricing.since", {
                  price: format.money(p.amount),
                  when: format.dateTime(p.effectiveFrom.toISOString(), outlet.timezone),
                })}
              </li>
            ))}
          </ul>
        </div>
      )}
      <LoadMore pages={history} />
      {pricing && (
        <PriceDialog
          outlet={outlet}
          productId={props.productId}
          variant={props.variant}
          onClose={() => setPricing(false)}
        />
      )}
    </li>
  );
}

/** Times are entered and shown in the outlet's timezone, whatever the manager's device uses. */
function PriceDialog(props: {
  outlet: OutletSummary;
  productId: string;
  variant: VariantView;
  onClose: () => void;
}) {
  const { outlet } = props;
  const { t, locale } = useMessages(catalogue);
  const queryClient = useQueryClient();
  const [amount, setAmount] = useState("");
  // Date picker + time field (outlet-local); together they form "YYYY-MM-DDTHH:MM".
  const [scheduleDate, setScheduleDate] = useState("");
  const [scheduleTime, setScheduleTime] = useState("");
  const scheduleAt = scheduleDate && scheduleTime ? `${scheduleDate}T${scheduleTime}` : "";
  const amountId = useId();
  const whenId = useId();
  const whenLabelId = useId();
  const timeId = useId();
  const scheduleErrorId = useId();
  const scheduleError =
    scheduleDate && !scheduleTime
      ? t("catalogue.pricing.needTime")
      : scheduleAt && zonedLocalToUtc(scheduleAt, outlet.timezone) <= new Date()
        ? t("catalogue.pricing.inPast")
        : null;

  /** Picking a day prefills the next quarter hour (today, outlet time) or 08:00, never a past 00:00. */
  function pickScheduleDate(day: string) {
    setScheduleDate(day);
    setScheduleTime(day ? defaultScheduleTime(day, new Date(), outlet.timezone) : "");
  }

  const schedule = useMutation({
    mutationFn: async () =>
      unwrap(
        await api.v1.outlets({ outletId: outlet.id }).prices.post({
          variantId: props.variant.id,
          amount,
          ...(scheduleAt
            ? { effectiveFrom: zonedLocalToUtc(scheduleAt, outlet.timezone).toISOString() }
            : {}),
        }),
      ),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: pricesQuery(outlet.id, props.variant.id).queryKey });
      // The first price lists the product at the outlet.
      await queryClient.invalidateQueries({
        queryKey: availabilityQuery(outlet.id, props.productId).queryKey,
      });
      toast.success(t(scheduleAt ? "catalogue.pricing.scheduled" : "catalogue.pricing.applied"));
      props.onClose();
    },
  });
  const blocked = schedule.isPending || !amount || scheduleError !== null;

  function submit(event: FormEvent) {
    event.preventDefault();
    if (!blocked) schedule.mutate();
  }

  return (
    <ResponsiveDialog
      open
      onOpenChange={(open) => !open && props.onClose()}
      title={t("catalogue.pricing.of", { name: props.variant.name })}
      description={t("catalogue.pricing.dialogHint", { outlet: outlet.name })}
      dismissible={!schedule.isPending}
      footer={
        <Button type="submit" form="price-form" disabled={blocked}>
          {t(
            schedule.isPending
              ? "common.saving"
              : scheduleAt
                ? "catalogue.pricing.schedule"
                : "catalogue.pricing.apply",
          )}
        </Button>
      }
    >
      <form id="price-form" className="flex flex-col gap-4" onSubmit={submit} noValidate>
        <div className="flex flex-col gap-1.5">
          <Label htmlFor={amountId}>{t("catalogue.pricing.amount")}</Label>
          <Input
            id={amountId}
            inputMode="numeric"
            value={amount}
            onChange={(e) => setAmount(e.target.value.replace(/\D/g, ""))}
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label id={whenLabelId} htmlFor={whenId}>
            {t("catalogue.pricing.startsAt", { timezone: outlet.timezone })}
          </Label>
          <DatePicker
            locale={locale}
            id={whenId}
            labelId={whenLabelId}
            value={scheduleDate}
            onChange={pickScheduleDate}
            placeholder={t("catalogue.pricing.now")}
          />
          {scheduleDate && (
            <>
              <Label htmlFor={timeId} className="sr-only">
                {t("catalogue.pricing.time")}
              </Label>
              <Input
                id={timeId}
                type="time"
                value={scheduleTime}
                onChange={(e) => setScheduleTime(e.target.value)}
                aria-invalid={scheduleError !== null}
                {...(scheduleError && { "aria-describedby": scheduleErrorId })}
              />
            </>
          )}
          {scheduleError && (
            <p id={scheduleErrorId} role="alert" className="text-destructive text-sm">
              {scheduleError}
            </p>
          )}
        </div>
        <FormError error={schedule.error} />
      </form>
    </ResponsiveDialog>
  );
}
